import React, { useState, useEffect } from "react";
import Modal from "./Modal";
import PrimaryButton from "../components/Buttons/PrimaryButton";
import SecondaryButton from "../components/Buttons/SecondaryButton";
import { submitToHubSpot } from "../services/hubspot";
import { useAuth } from "../utils/AuthContext";
import { useSendEmail } from "../hooks/useSendEmail";
import { contactConfirmationEmail } from "../emails";

interface ContactModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

interface FormData {
  name: string;
  email: string;
  message: string;
}

interface FormStatus {
  type: "idle" | "loading" | "success" | "error";
  message?: string;
}

const ContactModal: React.FC<ContactModalProps> = ({ isOpen, setIsOpen }) => {
  const { user } = useAuth();
  const { sendEmail } = useSendEmail();
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    message: "",
  });
  const [status, setStatus] = useState<FormStatus>({ type: "idle" });

  useEffect(() => {
    if (isOpen && user) {
      setFormData({
        name: user.name || "",
        email: user.email || "",
        message: "",
      });
      setStatus({ type: "idle" });
    }
  }, [isOpen, user]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus({ type: "loading" });

    try {
      await submitToHubSpot({
        name: formData.name,
        email: formData.email,
        message: formData.message,
      });

      await sendEmail({
        to: formData.email,
        template: contactConfirmationEmail,
      });

      setStatus({
        type: "success",
        message: "Thank you! Your message has been sent successfully.",
      });

      setTimeout(() => {
        setIsOpen(false);
        setFormData({
          name: user?.name || "",
          email: user?.email || "",
          message: "",
        });
        setStatus({ type: "idle" });
      }, 2000);
    } catch (error) {
      console.error("Error sending message:", error);
      setStatus({
        type: "error",
        message:
          "Failed to send message. Please try again or email us directly at contact@data-revival.com",
      });
    }
  };

  return (
    <Modal isOpen={isOpen} setIsOpen={setIsOpen}>
      <div className="w-full">
        <h3 className="text-base font-semibold tracking-tight text-gray-900 mb-0.5">
          Contact us
        </h3>
        <p className="text-sm text-gray-500 mb-5">
          Have a question or need help? Send us a message and we'll get back to you as soon as possible.
        </p>

        {status.type === "success" && (
          <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-4">
            {status.message}
          </p>
        )}

        {status.type === "error" && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4">
            {status.message}
          </p>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1.5">
              Name
            </label>
            <input
              id="name"
              type="text"
              name="name"
              required
              value={formData.name}
              onChange={handleChange}
              disabled={status.type === "loading"}
              className="w-full h-10 px-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1d365d] focus:border-[#1d365d] transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed"
              placeholder="Your name"
              readOnly
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
              Email
            </label>
            <input
              id="email"
              type="email"
              name="email"
              required
              value={formData.email}
              onChange={handleChange}
              disabled={status.type === "loading"}
              className="w-full h-10 px-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1d365d] focus:border-[#1d365d] transition-colors disabled:bg-gray-50 disabled:cursor-not-allowed"
              placeholder="your.email@example.com"
              readOnly
            />
          </div>

          <div>
            <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1.5">
              Message
            </label>
            <textarea
              id="message"
              name="message"
              required
              rows={6}
              value={formData.message}
              onChange={handleChange}
              disabled={status.type === "loading"}
              className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1d365d] focus:border-[#1d365d] transition-colors resize-none disabled:bg-gray-50 disabled:cursor-not-allowed"
              placeholder="Tell us about your question or issue..."
            />
            <div className="flex justify-between items-center text-xs text-gray-400 mt-1">
              <span>
                {formData.message.trim().length < 100 && "Please enter at least 100 characters."}
              </span>
              <span>{`${formData.message.trim().length}/100`}</span>
            </div>
          </div>

          <div className="flex gap-2 pt-2 justify-end">
            <SecondaryButton
              type="button"
              onClick={() => setIsOpen(false)}
              disabled={status.type === "loading"}
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              type="submit"
              disabled={status.type === "loading" || formData.message.trim().length < 100}
            >
              {status.type === "loading" ? "Sending..." : "Send message"}
            </PrimaryButton>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default ContactModal;
